import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { dealsAPI, clientsAPI, pipelineStagesAPI } from "../utils/api";
import { Plus, Search, LayoutGrid, List, Loader2, GripVertical, Trash2, Download } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DealModal from "../components/modals/DealModal";
import { toast } from "sonner";

const Deals = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState({});
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({
    stage: "",
    year: "",
    search: ""
  });

  const fetchClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll();
      const clientMap = {};
      response.data.forEach(c => {
        clientMap[c._id] = c.client_name;
      });
      setClients(clientMap);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  }, []);

  const fetchStages = useCallback(async () => {
    try {
      const response = await pipelineStagesAPI.getAll();
      setStages(response.data);
    } catch (error) {
      console.error("Error fetching stages:", error);
    }
  }, []);

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.stage && view === "list") params.stage = filters.stage;
      if (filters.year) params.year = filters.year;
      
      const response = await dealsAPI.getAll(params);
      let filtered = response.data;
      
      if (filters.search) {
        filtered = filtered.filter(d => 
          d.project_title?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setDeals(filtered);
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, view]);

  useEffect(() => {
    fetchDeals();
    fetchClients();
    fetchStages();
  }, [fetchDeals, fetchClients, fetchStages]);

  const handleEdit = (deal) => {
    setSelectedDeal(deal);
    setShowCreateModal(true);
  };

  const handleCreate = () => {
    setSelectedDeal(null);
    setShowCreateModal(true);
  };

  const handleDeleteDeal = async (e, dealId, dealTitle) => {
    e.stopPropagation();
    if (!window.confirm(`Delete deal "${dealTitle}"? This cannot be undone.`)) return;
    try {
      await dealsAPI.delete(dealId);
      toast.success("Deal deleted");
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete deal");
    }
  };

  const handleExportExcel = () => {
    const rows = deals.map(d => {
      const row = {
        "Deal ID": d.deal_id || "",
        "Project Title": d.project_title || "",
        "Client": clients[d.client_id] || d.client_id || "",
        "Stage": d.stage || "",
        "Year": d.year || "",
        "Total Value (RM)": d.total_value || 0,
        "Service Pillar": d.service_pillar || "",
        "Partner": d.partner_name || "",
        "Channel": d.channel || "",
        "Probability": d.probability != null ? `${d.probability}%` : "",
      };
      if (d.yearly_values?.length) {
        d.yearly_values.forEach(yv => {
          row[`Value ${yv.year}`] = yv.value || 0;
        });
      }
      return row;
    });

    if (!rows.length) { toast.error("No deals to export"); return; }

    const allKeys = new Set();
    rows.forEach(r => Object.keys(r).forEach(k => allKeys.add(k)));
    const headers = [...allKeys];

    const csvContent = [
      headers.join(","),
      ...rows.map(row =>
        headers.map(h => {
          const val = String(row[h] ?? "");
          return val.includes(",") || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
        }).join(",")
      )
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `HumanCap_Deals_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Deals exported successfully");
  };

  const formatCurrency = (value) => {
    return `RM ${(value / 1000).toFixed(0)}K`;
  };

  // Kanban drag handler
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const { draggableId, destination } = result;
    const newStage = destination.droppableId;
    
    // Find the deal
    const deal = deals.find(d => d._id === draggableId);
    if (!deal || deal.stage === newStage) return;

    // Optimistic update
    const oldDeals = [...deals];
    setDeals(deals.map(d => 
      d._id === draggableId ? { ...d, stage: newStage } : d
    ));

    try {
      await dealsAPI.updateStage(draggableId, newStage);
      toast.success(`Deal moved to ${newStage}`);
      fetchDeals(); // Refresh to get updated probability
    } catch (error) {
      console.error("Error updating deal stage:", error);
      toast.error("Failed to update deal stage");
      setDeals(oldDeals); // Revert
    }
  };

  // Group deals by stage for Kanban
  const dealsByStage = stages.reduce((acc, stage) => {
    acc[stage.stage] = deals.filter(d => d.stage === stage.stage);
    return acc;
  }, {});

  // Calculate stage totals
  const getStageTotals = (stageName) => {
    const stageDeals = dealsByStage[stageName] || [];
    const count = stageDeals.length;
    const total = stageDeals.reduce((sum, d) => sum + (d.total_value || 0), 0);
    return { count, total };
  };

  const getStageColor = (stage) => {
    const colors = {
      "Lead": "bg-slate-100 border-slate-300",
      "Qualified": "bg-blue-50 border-blue-200",
      "WIP": "bg-cyan-50 border-cyan-200",
      "Proposal Sent": "bg-indigo-50 border-indigo-200",
      "Follow-up 1": "bg-purple-50 border-purple-200",
      "Follow-up 2": "bg-purple-50 border-purple-200",
      "Negotiation": "bg-amber-50 border-amber-200",
      "Awaiting PO": "bg-orange-50 border-orange-200",
      "Won": "bg-green-50 border-green-200",
      "Lost": "bg-red-50 border-red-200",
      "Dropped": "bg-slate-50 border-slate-300"
    };
    return colors[stage] || "bg-slate-50 border-slate-200";
  };

  if (loading && deals.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="deals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Deals Pipeline</h1>
          <p className="text-slate-500 mt-1 text-sm">{deals.length} deals</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-sm text-slate-700"
            data-testid="export-deals-btn"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors self-start sm:self-auto"
            data-testid="new-deal-btn"
          >
            <Plus size={18} />
            New Deal
          </button>
        </div>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 sm:p-4 rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px] sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search deals..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-64"
              data-testid="deal-search"
            />
          </div>

          {/* Year Filter */}
          <select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: e.target.value ? parseInt(e.target.value) : "" })}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="year-filter"
          >
            <option value="">All Years</option>
            <option value="2022">2022</option>
            <option value="2023">2023</option>
            <option value="2024">2024</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>

          {/* Stage Filter (List view only) */}
          {view === "list" && (
            <select
              value={filters.stage}
              onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="stage-filter"
            >
              <option value="">All Stages</option>
              {stages.map(s => (
                <option key={s.stage} value={s.stage}>{s.stage}</option>
              ))}
            </select>
          )}
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setView("list")}
            className={`p-2 rounded-lg transition-colors ${
              view === "list" ? "bg-white shadow text-primary" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="list-view-btn"
          >
            <List size={20} />
          </button>
          <button
            onClick={() => setView("kanban")}
            className={`p-2 rounded-lg transition-colors ${
              view === "kanban" ? "bg-white shadow text-primary" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="kanban-view-btn"
          >
            <LayoutGrid size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      {view === "list" ? (
        /* List View */
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Deal ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Project Title</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Client</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Stage</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Year</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Value</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {deals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-slate-500">
                    No deals found
                  </td>
                </tr>
              ) : (
                deals.map((deal) => (
                  <tr 
                    key={deal._id} 
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer group"
                    onClick={() => handleEdit(deal)}
                    data-testid={`deal-row-${deal._id}`}
                  >
                    <td className="py-3 px-4 text-sm text-slate-600">{deal.deal_id}</td>
                    <td className="py-3 px-4 text-sm font-medium text-slate-900">{deal.project_title}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{clients[deal.client_id] || deal.client_id}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStageColor(deal.stage)}`}>
                        {deal.stage}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{deal.year}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-slate-900 text-right">
                      {formatCurrency(deal.total_value)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={(e) => handleDeleteDeal(e, deal._id, deal.project_title)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        data-testid={`delete-deal-${deal._id}`}
                        title="Delete deal"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Kanban View */
        <div className="kanban-wrapper" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <div 
              className="flex gap-3 pb-4 kanban-container" 
              style={{ 
                overflowX: 'scroll', 
                overflowY: 'visible',
                height: '100%'
              }} 
              data-testid="kanban-board"
            >
            {stages.map((stage) => {
              const { count, total } = getStageTotals(stage.stage);
              const stageDeals = dealsByStage[stage.stage] || [];
              
              return (
                <Droppable key={stage.stage} droppableId={stage.stage}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-shrink-0 w-72 bg-slate-50 rounded-lg border ${
                        snapshot.isDraggingOver ? "border-primary bg-primary/5" : "border-slate-200"
                      }`}
                      data-testid={`kanban-column-${stage.stage}`}
                    >
                      {/* Column Header */}
                      <div className="p-3 border-b border-slate-200">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-slate-900 text-sm">{stage.stage}</h3>
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                            {count}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {formatCurrency(total)} • {stage.probability}%
                        </p>
                      </div>

                      {/* Column Cards - No overflow scroll to fix drag-drop */}
                      <div className="p-2 min-h-[200px]">
                        {stageDeals.map((deal, index) => (
                          <Draggable key={deal._id} draggableId={deal._id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`bg-white rounded-lg border p-3 mb-2 cursor-grab active:cursor-grabbing transition-shadow ${
                                  snapshot.isDragging 
                                    ? "shadow-lg border-primary rotate-2" 
                                    : "border-slate-200 hover:shadow-md"
                                }`}
                                data-testid={`kanban-card-${deal._id}`}
                              >
                                <div 
                                  className="flex items-start justify-between gap-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(deal);
                                  }}
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-slate-500 mb-1">{deal.deal_id}</p>
                                    <h4 className="font-medium text-slate-900 text-sm truncate">
                                      {deal.project_title}
                                    </h4>
                                    <p className="text-xs text-slate-500 truncate">
                                      {clients[deal.client_id] || "Unknown Client"}
                                    </p>
                                  </div>
                                  <div className="text-slate-400 flex-shrink-0">
                                    <GripVertical size={16} />
                                  </div>
                                </div>
                                <div 
                                  className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(deal);
                                  }}
                                >
                                  <span className="text-xs text-slate-500">{deal.year}</span>
                                  <span className="text-sm font-semibold text-primary">
                                    {formatCurrency(deal.total_value)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        
                        {/* Add Deal Button in Column */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDeal(null);
                            setShowCreateModal(true);
                          }}
                          className="w-full py-2 text-sm text-slate-500 hover:text-primary hover:bg-white rounded-lg border border-dashed border-slate-300 transition-colors"
                          data-testid={`add-deal-${stage.stage}`}
                        >
                          + Add Deal
                        </button>
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
        </div>
      )}

      {/* Deal Modal */}
      <DealModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedDeal(null);
        }}
        deal={selectedDeal}
        onSuccess={fetchDeals}
      />
    </div>
  );
};

export default Deals;
